// @flow

import type {AttributeMetaData} from 'src/types/writeback';
import type {EntityType} from 'src/types/ats-entities';
import type {DynamicLabelExtra} from 'src/types/dynamic-labels';

import * as React from 'react';
import {useSelector} from 'react-redux';

import {useWritebackAttributesAsOptions} from 'src/hooks/useWritebackAllowedAttributes';
import {createDynamicLabelSelector} from 'src/selectors/dynamic-labels';

import VariablePickerSelect from 'src/components/lib/variable-picker/select-2.jsx';

import css from './variable-picker.css';


const selectVariablePickerOptions = createDynamicLabelSelector({
  depth: 2,
});

export default function LoadedVariablePicker({
  entityType,
  value,
  attributeMetadata,
  onChange,
  readOnly,
  error,
}: {
  entityType: EntityType,
  value: ?string,
  attributeMetadata: ?Array<AttributeMetaData>,
  onChange: (?AttributeMetaData) => mixed,
  readOnly?: mixed,
  error?: mixed,
}): React.Node {
  const options = useWritebackAttributesAsOptions(
    entityType,
    attributeMetadata || [],
  );

  const selectedOption = options.find((option) => option.attributeId === value);

  return (
    <VariablePickerSelect
      classNames={{container: css.container}}
      baseEntityType={entityType}
      options={options}
      values={selectedOption ? [selectedOption] : []}
      limit={1}
      onChange={(values) => {
        const attributeName = values[0]?.attributeName;
        onChange(
          attributeName &&
            attributeMetadata?.find(
              (attr) => attr.attribute_name === attributeName,
            ),
        );
      }}
      allowArbitraryValues={false}
      disabled={readOnly}
      error={error}
    />
  );
}

export function AllVariablePicker({
  entityType,
  value,
  onChange,
  readOnly,
  className,
  placeholder,
  hasLabel,
  error,
  inputProps,
  type,
}: {
  entityType: EntityType,
  value: ?string,
  onChange: (?DynamicLabelExtra) => mixed,
  readOnly?: mixed,
  className?: ?string,
  placeholder?: ?string,
  hasLabel?: boolean,
  error?: mixed,
  inputProps?: React.ElementProps<'input'>,
  type?: string,
}): React.Node {
  const options = useSelector((state) =>
    selectVariablePickerOptions(state, entityType),
  );
  const selectedOption = options.find((option) => option.value === value);

  const filteredOptions = React.useMemo(
    () =>
      ['phone', 'email'].includes(type)
        ? options.filter((o) => o.type === type)
        : options,
    [options, type],
  );

  return (
    <VariablePickerSelect
      classNames={{outerContainer: className}}
      baseEntityType={entityType}
      options={filteredOptions}
      values={selectedOption ? [selectedOption] : []}
      limit={1}
      onChange={(values) => {
        onChange(values[0]);
      }}
      allowArbitraryValues={false}
      disabled={readOnly}
      placeholder={placeholder}
      hasLabel={hasLabel}
      error={error}
      inputProps={inputProps}
    />
  );
}
